// The Agent feedback section: an agent's clarification, behavior and platform questions, and its
// proposals to move a tracked date, in the
// Next actions table's look. The questions waiting on the operator lead; the ones they
// have handled (answered, being applied, closed) sit below, collapsed under Addressed.
// A question asked in a conversation also shows there as a card (ConversationQuestion).
// See docs/Y2026-Q4/design-operator-questions-and-action-provenance.md section 4 and
// docs/Y2026-Q4/design-conversation-questions.md.
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  amazonAnswerSubject,
  type FeedbackQuestion,
  type FeedbackScope,
  isActionDateMove,
  lastAssignment,
  needsOperator,
  teamName,
  tabsShowing,
  useAnswerFeedbackQuestion,
  useFeedbackQuestions,
  useSkipFeedbackQuestion,
} from "../api/hooks/useAgentFeedback";
import { daysSince } from "../api/hooks/useProjectAgentStatus";
import { runName } from "../api/hooks/useRunNames";
import {
  type RunDescription,
  useRunsDescribe,
} from "../api/hooks/useRunsDescribe";
import { projectStatusPath, runLinkPath } from "../routes/learningAgentPaths";
import { formatDay } from "../utils/attributeFormat";
import { useHashOpen, useScrollToHash } from "../utils/useScrollToHash";
import { agentDisplayLabel } from "../utils/runParticipants";
import { sendOnEnter } from "../utils/sendShortcut";
import { Cited } from "./Cited";
import { AmazonAnswerForm } from "./AmazonAnswerForm";
import { DateMoveAnswer } from "./DateMoveAnswer";
import { isTextSelectionClick } from "./selectableButton";
import { SectionBlock, SectionCard } from "./SpecialistRowSections";
import styles from "./NextActionTable.module.css";
import own from "./AgentFeedbackSection.module.css";
import { Chevron } from "./Chevron";
import { FeedbackAssignChip } from "./FeedbackAssignee";

const KIND_LABEL: Record<FeedbackQuestion["kind"], string> = {
  clarification: "Clarification",
  behavior: "Behavior",
  platform: "Platform issue",
  date_move: "Date move",
  drive_addition: "Drive addition",
  amazon_answer: "Amazon's answer",
};

/** Closed by an operator without an answer. Must agree with ``was_skipped`` in
 * agent_feedback.py. */
const wasSkipped = (q: FeedbackQuestion): boolean =>
  q.status === "closed" && q.events.some((e) => e.kind === "skipped");

/** What the agent said changed, when it asked again after a skip. */
const askedAgainBecause = (q: FeedbackQuestion): string | null =>
  q.events.find((e) => e.kind === "asked" && e.after_skip)?.note ?? null;

const overdue = (q: FeedbackQuestion): boolean =>
  q.status === "open" && !!q.needed_by && (daysSince(q.needed_by) ?? 0) > 0;

/** The status the row shows, and its tone. */
const statusOf = (q: FeedbackQuestion): { text: string; tone: string } => {
  if (q.status === "closed" && q.kind === "date_move")
    return {
      text: !q.answer ? "Overtaken" : q.answer.keep ? "Kept" : "Moved",
      tone: styles.chipClosed,
    };
  if (wasSkipped(q)) return { text: "Skipped", tone: styles.chipClosed };
  if (q.status === "closed")
    return { text: q.answer ? "Applied" : "Closed", tone: styles.chipClosed };
  if (q.status === "answered")
    return { text: "Being applied", tone: styles.chip };
  if (q.status === "waiting")
    return { text: "Waiting on you", tone: `${styles.chip} ${styles.missed}` };
  if (overdue(q))
    return { text: "Overdue", tone: `${styles.chip} ${styles.missed}` };
  return { text: "Needs your answer", tone: `${styles.chip} ${styles.near}` };
};

/** The answer as the operator gave it: the options chosen (or, for a date move, the
 * date they picked), then their own words. */
const answerWords = (q: FeedbackQuestion): string =>
  [
    ...(q.answer?.options ?? []).map(
      (i) => q.options[i]?.label ?? `option ${i + 1}`,
    ),
    ...(q.kind === "date_move" && q.answer?.date && !q.answer.options.length
      ? [`Moved to ${formatDay(q.answer.date)}`]
      : []),
    ...(q.answer?.other ? [`"${q.answer.other}"`] : []),
  ].join("; ");

/** A tracked date's move: an action's is answered on the action (DateMoveAnswer there). */
const DateMoveDetail = ({ question }: { question: FeedbackQuestion }) => {
  const subject = question.subject;
  if (!subject) return null;
  return (
    <div className={own.answer}>
      <p>
        <s>{formatDay(subject.current)}</s> → {formatDay(subject.proposed)}.{" "}
        <Cited text={question.context} inline />
      </p>
      <DateMoveAnswer
        questionId={question.question_id}
        current={subject.current}
        proposed={subject.proposed}
        pushAllowed={subject.push_allowed}
      />
      <span className={styles.small}>
        The date in force doesn't move until you answer.
      </span>
    </div>
  );
};

// Every option a card (a radio, or a checkbox when several may apply) with its label
// over what it means, then Other with room for the operator's own words, then Skip with
// room for an optional reason, and one button. Choosing never sends on its own. Skip
// stands alone: picking it clears the other choices, and picking one of them clears it.
const OTHER = -1;
const SKIP = -2;

const AnswerForm = ({ question }: { question: FeedbackQuestion }) => {
  const { user } = useKindeAuth();
  const answer = useAnswerFeedbackQuestion();
  const skip = useSkipFeedbackQuestion();
  const [picked, setPicked] = useState<number[]>([]);
  const [other, setOther] = useState("");
  const [reason, setReason] = useState("");
  const multi = question.multi_select;
  // Applied the moment it is answered, so there is nothing for words to say.
  const platformApplied = question.kind === "drive_addition";
  const skipPicked = picked.includes(SKIP);
  const otherPicked = picked.includes(OTHER);
  const options = picked.filter((i) => i >= 0);
  const words = otherPicked ? other.trim() : "";
  const ready = skipPicked || (otherPicked ? words !== "" : options.length > 0);
  const busy = answer.isLoading || skip.isLoading;
  const error = answer.error ?? skip.error;
  const agent = agentDisplayLabel(question.agent);
  const submit = () => {
    if (!ready || busy) return;
    const actor_email = user?.email ?? "";
    if (skipPicked)
      skip.mutate({
        question_id: question.question_id,
        note: reason.trim(),
        actor_email,
      });
    else
      answer.mutate({
        question_id: question.question_id,
        options,
        other: words,
        actor_email,
      });
  };
  const toggle = (index: number, on: boolean) =>
    setPicked((now) =>
      !multi || index === SKIP
        ? on
          ? [index]
          : []
        : on
          ? [...now.filter((i) => i !== SKIP), index]
          : now.filter((i) => i !== index),
    );
  const card = (index: number, label: string, description: string) => {
    const on = picked.includes(index);
    return (
      <label
        className={`${own.option} ${on ? own.picked : ""} ${
          index < 0 ? own.otherOption : ""
        }`}
      >
        <input
          type={multi ? "checkbox" : "radio"}
          name={question.question_id}
          checked={on}
          disabled={busy}
          onChange={(e) => toggle(index, e.target.checked)}
        />
        <span>
          <span className={own.optionLabel}>{label}</span>
          {description && (
            <span className={own.optionDescription}>{description}</span>
          )}
        </span>
      </label>
    );
  };
  return (
    <div className={own.answer}>
      <div className={own.options}>
        {question.options.map((option, index) => (
          <div key={index}>{card(index, option.label, option.description)}</div>
        ))}
        {!platformApplied && (
          <div>
            {card(OTHER, "Something else - answer in your own words", "")}
            {otherPicked && (
              <textarea
                className={own.other}
                placeholder="Your answer"
                value={other}
                onChange={(e) => setOther(e.target.value)}
                onKeyDown={sendOnEnter(submit)}
                rows={3}
                autoFocus
              />
            )}
          </div>
        )}
        {!platformApplied && (
          <div>
            {card(SKIP, "Skip - don't answer this one", "")}
            {skipPicked && (
              <textarea
                className={own.other}
                placeholder="Why skip it? Optional"
                aria-label="Why skip it? Optional"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                onKeyDown={sendOnEnter(submit)}
                rows={3}
                maxLength={500}
                autoFocus
              />
            )}
          </div>
        )}
      </div>
      <div className={own.submitRow}>
        <button
          type="button"
          className={`${styles.button} ${styles.primary}`}
          disabled={busy || !ready}
          onClick={submit}
        >
          {busy ? "Applying…" : "Apply answer"}
        </button>
        <span className={styles.small}>
          {skipPicked
            ? `It closes unanswered. ${agent} asks it again only if something changes, and says what.`
            : ready && platformApplied
              ? "It is applied as soon as you answer."
              : ready
              ? `${agent} will apply it, and may come back to you with a follow-up.`
              : otherPicked
                ? "Write your answer to continue."
                : multi
                  ? "Choose any that apply to continue."
                  : "Choose an option to continue."}
        </span>
      </div>
      {error && <p className={styles.error}>{error.message}</p>}
    </div>
  );
};

/** The context and evidence behind a question, folded away under the decision. Its
 * body mounts only once opened, so a folded one looks up no citations. */
const WhyAsking = ({ question }: { question: FeedbackQuestion }) => {
  const [open, setOpen] = useState(false);
  return (
    <details
      className={own.why}
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary>Why {agentDisplayLabel(question.agent)} is asking</summary>
      {open && (
        <div className={own.whyBody}>
          <section className={styles.section}>
            <h5>Context</h5>
            <Cited text={question.context} />
          </section>
          {question.evidence.length > 0 && (
            <section className={styles.section}>
              <h5>What it rests on</h5>
              {question.evidence.map((e) => (
                <Cited key={e} text={e} />
              ))}
            </section>
          )}
        </div>
      )}
    </details>
  );
};

/** ``here`` is the conversation on screen, which names itself rather than linking. */
const ConversationLink = ({
  runId,
  runs,
  here,
}: {
  runId: string;
  runs: Map<string, RunDescription>;
  here: string | null;
}) => {
  if (runId === here) return <>this conversation</>;
  const run = runs.get(runId);
  return (
    <Link to={runLinkPath(runId)}>{run ? runName(run) : "Conversation"}</Link>
  );
};

/** "Assigned to John by Noaa, 25 Oct", from the latest assignment on the question. */
const AssignedLine = ({
  question,
  team,
}: {
  question: FeedbackQuestion;
  team: Record<string, string>;
}) => {
  const last = lastAssignment(question);
  if (!last) return null;
  const by = teamName(team, last.by);
  const when = formatDay(last.at.slice(0, 10));
  return (
    <p className={styles.small}>
      {last.to
        ? `Assigned to ${teamName(team, last.to)} by ${by}, ${when}.`
        : `Assignment cleared by ${by}, ${when}.`}
    </p>
  );
};

const Detail = ({
  question,
  runs,
  here,
  team,
}: {
  question: FeedbackQuestion;
  runs: Map<string, RunDescription>;
  /** The conversation on screen, or null on an Agent feedback section. */
  here: string | null;
  team: Record<string, string>;
}) => (
  <div className={`${styles.sections} ${own.detail}`}>
    <AssignedLine question={question} team={team} />
    {askedAgainBecause(question) && (
      <p className={styles.small}>{askedAgainBecause(question)}</p>
    )}
    {question.status === "open" && (
      <section className={styles.section}>
        {/* An Amazon answer heads its own options, below what Amazon said. */}
        {!amazonAnswerSubject(question) && (
          <h5>
            {question.kind === "date_move"
              ? "Move the date?"
              : question.multi_select
                ? "Choose any that apply"
                : "Choose one"}
          </h5>
        )}
        {question.needed_by && (
          <p className={styles.small}>
            Needed by {formatDay(question.needed_by)}.
          </p>
        )}
        {question.closed_note && question.kind !== "date_move" && (
          <p className={styles.small}>
            Your last answer could not be applied: {question.closed_note}
          </p>
        )}
        {question.kind === "date_move" ? (
          <DateMoveDetail question={question} />
        ) : amazonAnswerSubject(question) ? (
          <AmazonAnswerForm question={question} />
        ) : (
          <AnswerForm question={question} />
        )}
      </section>
    )}
    {question.answer && (
      <section className={`${styles.section} ${styles.closedBox}`}>
        <h5>Your answer</h5>
        <p>
          {answerWords(question)}
          {question.answered_by && <> - {question.answered_by}</>}
          {question.answered_at && (
            <>, {formatDay(question.answered_at.slice(0, 10))}</>
          )}
        </p>
        {question.review_conversation_id && (
          <p className={styles.small}>
            {question.status === "waiting"
              ? "The agent asked you a follow-up. Answer it in "
              : question.status === "closed"
                ? "Applied in "
                : "Being applied in "}
            <ConversationLink
              runId={question.review_conversation_id}
              runs={runs}
              here={here}
            />
          </p>
        )}
      </section>
    )}
    {question.status === "closed" && question.closed_note && (
      <section className={`${styles.section} ${styles.closedBox}`}>
        <h5>How it closed</h5>
        <p>{question.closed_note}</p>
      </section>
    )}
    {/* An Amazon answer's parts are shown open above the choice, never folded. */}
    {!amazonAnswerSubject(question) && <WhyAsking question={question} />}
    {amazonAnswerSubject(question) ? (
      <p className={styles.small}>Asked from the Amazon overview.</p>
    ) : question.raised_by_run_id !== here && (
      <p className={styles.small}>
        Asked in{" "}
        <ConversationLink
          runId={question.raised_by_run_id}
          runs={runs}
          here={here}
        />
        .
      </p>
    )}
  </div>
);

/** ``section``: an Agent feedback section's columns (Asked, Status). ``open``: My actions'
 * Open agent feedback, where every row waits on the operator, so no Status: the Site and
 * Specialist columns instead, and a follow-up said on the row. */
export type QuestionColumns = "section" | "open";

/** Who holds the question: the chip while it is still in play, their name once settled. */
const AssignedCell = ({
  question,
  team,
}: {
  question: FeedbackQuestion;
  team: Record<string, string>;
}) => (
  <td>
    {question.status === "closed" ? (
      question.assignee && (
        <span className={styles.small}>{teamName(team, question.assignee)}</span>
      )
    ) : (
      <FeedbackAssignChip question={question} team={team} />
    )}
  </td>
);

const QuestionRow = ({
  question,
  columns,
  showProjects,
  open,
  onToggle,
  runs,
  team,
}: {
  question: FeedbackQuestion;
  columns: QuestionColumns;
  showProjects: boolean;
  open: boolean;
  onToggle: () => void;
  runs: Map<string, RunDescription>;
  team: Record<string, string>;
}) => {
  const status = statusOf(question);
  const projects = question.project_ids
    .map((p) => question.project_names[p] ?? p)
    .join(", ");
  const waiting = needsOperator(question);
  const listed = columns === "open";
  const agent = agentDisplayLabel(question.agent);
  return (
    <tbody
      id={question.question_id}
      className={`${styles.item} ${!listed && overdue(question) ? styles.missed : ""} ${
        waiting ? own.waitingItem : own.addressedItem
      }`}
    >
      <tr
        className={styles.row}
        onClick={(e) => {
          if (!isTextSelectionClick(e)) onToggle();
        }}
      >
        <td className={styles.titleCell}>
          <button type="button" className={styles.toggle} aria-expanded={open}>
            <span className={`${styles.caret} ${own.caret}`} aria-hidden="true">
              <Chevron tree open={open} />
            </span>
            <span className={styles.titleText}>
              <strong>{question.question}</strong>
              <span className={styles.typeLine}>
                {KIND_LABEL[question.kind]} from {agent}
                {showProjects && projects ? ` · ${projects}` : ""}
                {listed && question.status === "waiting" && (
                  <>
                    {" · "}
                    <span className={own.followUp}>Follow-up to your answer</span>
                  </>
                )}
              </span>
            </span>
          </button>
        </td>
        {!listed && <AssignedCell question={question} team={team} />}
        {listed && (
          <>
            <td>
              {(question.sites ?? []).map(({ project_id }) => (
                <div key={project_id}>
                  <Link
                    to={projectStatusPath(project_id)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {question.project_names[project_id] ?? project_id}
                  </Link>
                </div>
              ))}
            </td>
            <td>
              <span className={styles.small}>{tabsShowing(question).join(", ")}</span>
            </td>
            <AssignedCell question={question} team={team} />
          </>
        )}
        <td>
          <span className={styles.small}>
            {formatDay(question.created_at.slice(0, 10))}
          </span>
        </td>
        {!listed && (
          <td>
            <span className={status.tone}>{status.text}</span>
          </td>
        )}
      </tr>
      {open && (
        <tr className={styles.detailRow}>
          <td colSpan={listed ? 5 : 4}>
            <Detail question={question} runs={runs} here={null} team={team} />
          </td>
        </tr>
      )}
    </tbody>
  );
};

export const QuestionTable = ({
  questions,
  head,
  columns,
  ...row
}: {
  questions: FeedbackQuestion[];
  head: boolean;
  columns: QuestionColumns;
  showProjects: boolean;
  openIds: Set<string>;
  toggle: (id: string) => void;
  runs: Map<string, RunDescription>;
  /** ``{email: name}``: who a question may be given to. */
  team: Record<string, string>;
}) => (
  <div className={styles.scroll}>
    <table className={styles.table}>
      <colgroup>
        <col className={styles.titleCol} />
        <col />
        <col />
        <col />
        {columns === "open" && <col />}
      </colgroup>
      {head && (
        <thead>
          {columns === "open" ? (
            <tr>
              <th>Question</th>
              <th>Site</th>
              <th>Specialist</th>
              <th>Assigned</th>
              <th>Asked</th>
            </tr>
          ) : (
            <tr>
              <th>Question</th>
              <th>Assigned</th>
              <th>Asked</th>
              <th>Status</th>
            </tr>
          )}
        </thead>
      )}
      {questions.map((q) => (
        <QuestionRow
          key={q.question_id}
          question={q}
          columns={columns}
          showProjects={row.showProjects}
          open={row.openIds.has(q.question_id)}
          onToggle={() => row.toggle(q.question_id)}
          runs={row.runs}
          team={row.team}
        />
      ))}
    </table>
  </div>
);

/** The section on a project's specialist page, or on a prompt page (``showProjects``). */
export const AgentFeedbackSection = ({
  scope,
  showProjects,
}: {
  scope: FeedbackScope;
  showProjects: boolean;
}) => {
  const query = useFeedbackQuestions(scope);
  // A link to a question (from a run's page) opens it and scrolls to it. The page's own
  // scroll can run before these rows exist: they load on a request of their own.
  const { landedId, openIds, toggle } = useHashOpen();
  useScrollToHash(!!query.data && landedId.startsWith("fq-"));
  // A move of an action's date is answered on the action, so it is not asked twice.
  const questions = (query.data?.questions ?? []).filter(
    (q) => !isActionDateMove(q),
  );
  const { byId: runs } = useRunsDescribe(
    questions.flatMap((q) => [
      q.raised_by_run_id,
      ...(q.review_conversation_id ? [q.review_conversation_id] : []),
    ]),
  );
  // Collapsed until opened, unless a link landed on a question inside it.
  const [addressedOpen, setAddressedOpen] = useState<boolean | null>(null);
  if (query.isError)
    return (
      <p className={styles.error}>Could not load the agent's questions.</p>
    );
  // Asked back first, then unanswered; most recently handled first below. Oldest first
  // within each waiting group.
  const waiting = questions
    .filter(needsOperator)
    .sort((a, b) =>
      a.status === b.status ? 0 : a.status === "waiting" ? -1 : 1,
    );
  const addressed = questions
    .filter((q) => !needsOperator(q))
    .sort((a, b) =>
      (b.closed_at ?? b.answered_at ?? "").localeCompare(
        a.closed_at ?? a.answered_at ?? "",
      ),
    );
  const showAddressed =
    addressedOpen ?? addressed.some((q) => q.question_id === landedId);
  const table = {
    showProjects,
    openIds,
    toggle,
    runs,
    columns: "section" as const,
    team: query.data?.team ?? {},
  };
  return (
    <SectionCard title="Agent feedback" aside={null} id="agent-feedback">
      <SectionBlock>
        {query.isLoading ? (
          <p className={styles.muted}>Loading…</p>
        ) : questions.length === 0 ? (
          <p className={styles.muted}>No questions from the agent.</p>
        ) : (
          <div className={own.groups}>
            {waiting.length > 0 ? (
              <QuestionTable questions={waiting} head {...table} />
            ) : (
              <p className={styles.muted}>Nothing waiting on you.</p>
            )}
            {addressed.length > 0 && (
              <div className={own.addressed}>
                <button
                  type="button"
                  className={own.addressedToggle}
                  aria-expanded={showAddressed}
                  onClick={() => setAddressedOpen(!showAddressed)}
                >
                  <span aria-hidden="true">
                    <Chevron tree open={showAddressed} />
                  </span>
                  Addressed ({addressed.length})
                </button>
                {showAddressed && (
                  <QuestionTable
                    questions={addressed}
                    head={false}
                    {...table}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </SectionBlock>
    </SectionCard>
  );
};

const NO_RUNS = new Map<string, RunDescription>();
// A conversation's card names people by email; its page holds no Team list.
const NO_TEAM: Record<string, string> = {};

/** A question as a card under the conversation reply that asked it: open while it waits
 * on the operator, folded once handled. Answering here or under Agent feedback is one
 * answer, applied in this conversation. */
export const ConversationQuestion = ({
  question,
  runId,
}: {
  question: FeedbackQuestion;
  runId: string;
}) => {
  const waiting = needsOperator(question);
  const [open, setOpen] = useState<boolean | null>(null);
  const shown = open ?? waiting;
  const status = statusOf(question);
  return (
    <section
      id={question.question_id}
      className={`${own.card} ${waiting ? own.cardWaiting : ""}`}
      aria-label={`Question from ${agentDisplayLabel(question.agent)}`}
    >
      <button
        type="button"
        className={own.cardHead}
        aria-expanded={shown}
        onClick={() => setOpen(!shown)}
      >
        <span className={own.cardCaret} aria-hidden="true">
          <Chevron tree open={shown} />
        </span>
        <span className={styles.titleText}>
          <strong>{question.question}</strong>
          <span className={styles.typeLine}>
            {KIND_LABEL[question.kind]} from {agentDisplayLabel(question.agent)}
            {waiting ? " · also waiting under Agent feedback" : ""}
          </span>
        </span>
        <span className={status.tone}>{status.text}</span>
      </button>
      {shown && <Detail question={question} runs={NO_RUNS} here={runId} team={NO_TEAM} />}
    </section>
  );
};
